import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Award, Mail, Download, BookOpen, Sparkles, Check, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { playWin } from "@/lib/sound";

const KEY = "chronos.eveil.passport.v1";

export function markEveilPassportSeen() {
  try { localStorage.setItem(KEY, JSON.stringify({ at: Date.now() })); } catch (e) {}
}

/**
 * Modal shown after a child completes both Éveil chapters (101 + 102).
 * - Celebrates the child
 * - Captures parent email (opt-in RGPD-K)
 * - Delivers 2 PDF downloads: certificate + parent guide
 */
export default function EveilPassportModal({ open, onClose, player }) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState("celebrate"); // celebrate → form → downloads
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [urls, setUrls] = useState(null);

  if (!player) return null;

  const requestPassport = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/eveil/passport", {
        player_id: player.id,
        parent_email: email.trim() || null,
        consent: !!email.trim() && consent,
        lang: i18n.language?.slice(0, 2) || "fr",
      });
      const backend = process.env.REACT_APP_BACKEND_URL;
      setUrls({
        cert: `${backend}${data.certificate_url}`,
        guide: `${backend}${data.guide_url}`,
      });
      setStep("downloads");
      playWin();
      markEveilPassportSeen();
    } catch (e) {
      const msg = e?.response?.data?.detail || t("eveil.passport.err");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const skipEmail = async () => {
    setEmail("");
    setConsent(false);
    await requestPassport();
  };

  const closeAll = () => {
    markEveilPassportSeen();
    setStep("celebrate");
    setEmail("");
    setUrls(null);
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-center justify-center bg-stone-950/85 backdrop-blur-sm p-4"
          data-testid="eveil-passport-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-lg glass-strong rounded-3xl p-6 sm:p-8 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/25 via-transparent to-rose-500/15" aria-hidden />
            <button
              onClick={closeAll}
              className="absolute top-3 right-3 text-stone-400 hover:text-stone-100 p-2 z-10"
              data-testid="eveil-passport-close"
              aria-label={t("common.close")}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative z-10">
              {step === "celebrate" && (
                <div className="text-center" data-testid="eveil-passport-celebrate">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="text-7xl mb-3 shard-glow inline-block"
                    aria-hidden
                  >
                    🌱
                  </motion.div>
                  <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">
                    {t("eveil.passport.kicker")}
                  </div>
                  <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-black text-stone-50">
                    {t("eveil.passport.celebrate_title", { name: player.name })}
                  </h2>
                  <p className="mt-3 text-stone-300 leading-relaxed">
                    {t("eveil.passport.celebrate_desc")}
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-3 text-left">
                    <div className="glass rounded-2xl p-4">
                      <Award className="w-6 h-6 text-amber-300 mb-1.5" />
                      <div className="font-serif text-sm font-bold text-stone-50">
                        {t("eveil.passport.gift_cert_title")}
                      </div>
                      <div className="text-xs text-stone-400 mt-1">
                        {t("eveil.passport.gift_cert_desc")}
                      </div>
                    </div>
                    <div className="glass rounded-2xl p-4">
                      <BookOpen className="w-6 h-6 text-emerald-300 mb-1.5" />
                      <div className="font-serif text-sm font-bold text-stone-50">
                        {t("eveil.passport.gift_guide_title")}
                      </div>
                      <div className="text-xs text-stone-400 mt-1">
                        {t("eveil.passport.gift_guide_desc")}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => setStep("form")}
                    className="mt-6 bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-8 py-6 h-auto"
                    data-testid="eveil-passport-continue"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t("eveil.passport.claim_cta")}
                  </Button>
                </div>
              )}

              {step === "form" && (
                <div data-testid="eveil-passport-form">
                  <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">
                    {t("eveil.passport.form_kicker")}
                  </div>
                  <h3 className="mt-1 font-serif text-2xl font-bold text-stone-50">
                    {t("eveil.passport.form_title")}
                  </h3>
                  <p className="mt-2 text-sm text-stone-300 leading-relaxed">
                    {t("eveil.passport.form_desc")}
                  </p>

                  <label className="block mt-5 text-xs uppercase tracking-widest text-stone-400 font-bold">
                    {t("eveil.passport.email_label")}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("eveil.passport.email_placeholder")}
                    className="mt-2 w-full rounded-full px-5 py-3 bg-stone-950/60 border border-white/15 text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-amber-400/60"
                    data-testid="eveil-passport-email"
                  />

                  <label className="mt-4 flex items-start gap-2 text-xs text-stone-400 leading-relaxed cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 accent-amber-400"
                      data-testid="eveil-passport-consent"
                    />
                    <span>{t("eveil.passport.consent_label")}</span>
                  </label>

                  <div className="mt-6 flex gap-3 flex-wrap">
                    <Button
                      onClick={requestPassport}
                      disabled={submitting || (!!email.trim() && !consent)}
                      className="bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-6 py-3 h-auto flex-1"
                      data-testid="eveil-passport-submit"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {submitting ? t("eveil.passport.sending") : t("eveil.passport.form_cta")}
                    </Button>
                    <button
                      onClick={skipEmail}
                      disabled={submitting}
                      className="text-xs text-stone-400 hover:text-stone-200 self-center"
                      data-testid="eveil-passport-skip"
                    >
                      {t("eveil.passport.skip")}
                    </button>
                  </div>

                  <div className="mt-4 flex items-start gap-2 text-[10px] text-stone-500 leading-relaxed">
                    <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{t("eveil.passport.privacy_note")}</span>
                  </div>
                </div>
              )}

              {step === "downloads" && urls && (
                <div data-testid="eveil-passport-downloads" className="text-center">
                  <Check className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                  <h3 className="font-serif text-2xl font-bold text-stone-50">
                    {t("eveil.passport.done_title")}
                  </h3>
                  <p className="mt-2 text-sm text-stone-300 max-w-md mx-auto">
                    {t("eveil.passport.done_desc")}
                  </p>
                  <div className="mt-6 space-y-3">
                    <a href={urls.cert} target="_blank" rel="noopener noreferrer" data-testid="eveil-passport-dl-cert">
                      <Button className="w-full bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full px-5 py-6 h-auto">
                        <Download className="w-4 h-4 mr-2" />
                        {t("eveil.passport.dl_cert")}
                      </Button>
                    </a>
                    <a href={urls.guide} target="_blank" rel="noopener noreferrer" data-testid="eveil-passport-dl-guide">
                      <Button className="w-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-bold rounded-full px-5 py-6 h-auto">
                        <BookOpen className="w-4 h-4 mr-2" />
                        {t("eveil.passport.dl_guide")}
                      </Button>
                    </a>
                  </div>
                  <button
                    onClick={closeAll}
                    className="mt-6 text-xs text-stone-400 hover:text-stone-200"
                    data-testid="eveil-passport-done-close"
                  >
                    {t("eveil.passport.close")}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
